import * as v from "@valibot/valibot";

// ============================================================
// API Schemas
// ============================================================

import { Hex, UnsignedInteger } from "../../_schemas.ts";

/**
 * This action does not do anything (no operation), but causes the nonce to be marked as used.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#invalidate-pending-nonce-noop
 */
export const NoopRequest = /* @__PURE__ */ (() => {
  return v.object({
    /** Action to perform. */
    action: v.object({
      /** Type of action. */
      type: v.literal("noop"),
    }),
    /** Nonce (timestamp in ms) used to prevent replay attacks. */
    nonce: UnsignedInteger,
    /** ECDSA signature components. */
    signature: v.object({
      /** First 32-byte component. */
      r: v.pipe(Hex, v.length(66)),
      /** Second 32-byte component. */
      s: v.pipe(Hex, v.length(66)),
      /** Recovery identifier. */
      v: v.picklist([27, 28]),
    }),
    /** Expiration time of the action. */
    expiresAfter: v.optional(UnsignedInteger),
  });
})();
export type NoopRequest = v.InferOutput<typeof NoopRequest>;

/**
 * Successful response without specific data or error response.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#invalidate-pending-nonce-noop
 */
export type NoopResponse =
  | {
    /** Successful status. */
    status: "ok";
    /** Response details. */
    response: {
      /** Type of response. */
      type: "default";
    };
  }
  | {
    /** Error status. */
    status: "err";
    /** Error message. */
    response: string;
  };

// ============================================================
// Execution Logic
// ============================================================

import { parse } from "../../../_base.ts";
import { canonicalize } from "../../../signing/mod.ts";
import {
  type ExchangeConfig,
  type ExcludeErrorResponse,
  executeL1Action,
  type ExtractRequestOptions,
} from "./_base/mod.ts";

/** Schema for action fields (excludes request-level system fields). */
const NoopActionSchema = /* @__PURE__ */ (() => {
  return v.object(NoopRequest.entries.action.entries);
})();

/** Action parameters for the {@linkcode noop} function. */
export type NoopParameters = {
  /** Nonce used for this call; takes precedence over `nonceManager`. */
  nonce: number;
};

/** Request options for the {@linkcode noop} function. */
export type NoopOptions = ExtractRequestOptions<v.InferInput<typeof NoopRequest>>;

/** Successful variant of {@linkcode NoopResponse} without errors. */
export type NoopSuccessResponse = ExcludeErrorResponse<NoopResponse>;

/**
 * This action does not do anything (no operation), but causes the nonce to be marked as used.
 *
 * Signing: L1 Action.
 *
 * @param config General configuration for Exchange API requests.
 * @param params Parameters specific to the API request.
 * @param opts Request execution options.
 * @return Successful response without specific data.
 *
 * @throws {ValidationError} When the request parameters fail validation (before sending).
 * @throws {TransportError} When the transport layer throws an error.
 * @throws {ApiRequestError} When the API returns an unsuccessful response.
 *
 * @example
 * ```ts
 * import { HttpTransport } from "@nktkas/hyperliquid";
 * import { noop } from "@nktkas/hyperliquid/api/exchange";
 * import { privateKeyToAccount } from "npm:viem/accounts";
 *
 * const wallet = privateKeyToAccount("0x..."); // viem or ethers
 * const transport = new HttpTransport(); // or `WebSocketTransport`
 *
 * await noop(
 *   { transport, wallet },
 *   { nonce: 1730000000000 },
 * );
 * ```
 *
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#invalidate-pending-nonce-noop
 */
export function noop(
  config: ExchangeConfig,
  params: NoopParameters,
  opts?: NoopOptions,
): Promise<NoopSuccessResponse> {
  const action = canonicalize(
    NoopActionSchema,
    parse(NoopActionSchema, { type: "noop" }),
  );
  return executeL1Action({ ...config, nonceManager: () => params.nonce }, action, opts);
}
